import ConfirmModal from "@app/components/confirm-modal";
import Table from "@app/components/table";
import { ColumnConfig, Order } from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  BUCKET_URL_KEY,
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  REGEX_URL,
  RoutePath,
  SystemMessage,
} from "@app/constants";
import SocialPostsService from "@app/services/http/social-posts.service";
import { openPortalDialog } from "@app/services/modal.service";
import { Meta, SocialPost } from "@app/types";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import StorageService from "@core/services/storage/storage.service";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

function AvatarColumn({ data }: { data: SocialPost }) {
  const s3Url = StorageService.getObject(BUCKET_URL_KEY);

  const thumbnail = data.authorThumbnail
    ? data.authorThumbnail.match(REGEX_URL)
      ? data.authorThumbnail
      : s3Url + data.authorThumbnail
    : "";

  return (
    <div className="flex items-center justify-center w-[48px] min-w-[48px]">
      <img
        src={thumbnail}
        alt={data.authorName}
        className="w-9 h-9 rounded-full object-cover"
      />
    </div>
  );
}

function NameColumn({ data }: { data: SocialPost }) {
  return (
    <div
      className="truncate min-w-[180px] max-w-[220px]"
      title={data.authorName}
    >
      {data.authorName}
    </div>
  );
}

type SocialPostWithMaybeContent = SocialPost & { content?: string };

function ContentColumn({ data }: { data: SocialPostWithMaybeContent }) {
  return (
    <div
      className="min-w-[260px] max-w-[420px]"
      title={data.content || ""}
      style={{
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical" as any,
        overflow: "hidden",
      }}
    >
      {data.content || ""}
    </div>
  );
}

function StatusPillColumn({ data }: { data: SocialPost }) {
  // Map status to label and color according to design
  const statusMap: Record<number, { label: string; className: string }> = {
    1: { label: "Approve", className: "bg-[#E8FFF4] text-[#00A653]" },
    2: { label: "Pending", className: "bg-[#E7F0FF] text-[#246BFD]" },
    3: { label: "Reject", className: "bg-[#FFEAEA] text-[#D14343]" },
  };

  const meta = statusMap[Number(data.status)] || statusMap[2];

  return (
    <span className={`${meta.className} px-3 py-1 rounded text-xs`}>
      {meta.label}
    </span>
  );
}

function ZIndexColumn({ data }: { data: SocialPost }) {
  return <div className="min-w-[80px]">{data.zindex ?? "-"}</div>;
}

function NumberColumn({ value }: { value: number | undefined }) {
  return (
    <div className="text-center min-w-[80px]">
      {typeof value === "number" ? value : 0}
    </div>
  );
}

function FeedManagement() {
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [update, forceUpdate] = useForceUpdate();
  const navigate = useNavigate();

  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    SocialPostsService.getList(requestParams, abortController.signal).then(
      (res) => {
        setPosts(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }
      }
    );
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleEditClick = (post: SocialPost) => {
    navigate(
      `/${RoutePath.FEED_MANAGEMENT}/${RoutePath.UPDATE_FEED}/${post.id}`
    );
  };

  const handleDeleteClick = (id: number) => {
    if (id) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_FEED_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          SocialPostsService.delete(id).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  const columnConfig: ColumnConfig[] = [
    { label: "Id", dataKey: "id", width: "120px", hasOrder: true },
    {
      label: "Ảnh",
      dataKey: "authorThumbnail",
      width: "80px",
      component: AvatarColumn,
    },
    {
      label: "Họ và tên",
      dataKey: "authorName",
      width: "220px",
      component: NameColumn,
      hasOrder: true,
    },
    {
      label: "Content",
      dataKey: "content",
      width: "420px",
      component: ContentColumn,
    },
    {
      label: "Status",
      dataKey: "status",
      width: "140px",
      component: StatusPillColumn,
      hasOrder: true,
    },
    {
      label: "zIndex",
      dataKey: "zindex",
      width: "110px",
      component: ZIndexColumn,
      hasOrder: true,
    },
    {
      label: "likeCount",
      dataKey: "likeCount",
      width: "120px",
      component: ({ data }: { data: SocialPost }) => (
        <NumberColumn value={data.likeCount} />
      ),
      hasOrder: true,
    },
    {
      label: "shareCount",
      dataKey: "shareCount",
      width: "120px",
      component: ({ data }: { data: SocialPost }) => (
        <NumberColumn value={data.shareCount} />
      ),
      hasOrder: true,
    },
    {
      label: "commentCount",
      dataKey: "commentCount",
      width: "140px",
      component: ({ data }: { data: SocialPost }) => (
        <NumberColumn value={data.commentCount} />
      ),
      hasOrder: true,
    },
    {
      label: "reportCount",
      dataKey: "reportCount",
      width: "140px",
      component: ({ data }: { data: SocialPost }) => (
        <NumberColumn value={(data as any).reportCount} />
      ),
    },
  ];

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách feed</div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách feed"
          onInputChange={onSearchChange}
        />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={posts}
            meta={meta}
            hasAction
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            hasComment
            onCommentClick={(post) =>
              navigate(
                `/${RoutePath.FEED_MANAGEMENT}/${RoutePath.UPDATE_FEED}/${post.id}/comments`
              )
            }
            tableClassName="w-[1400px]"
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default FeedManagement;
